import React from "react";
import { Link } from "gatsby";
import RevealAnimation from "../RevealAnimation";
import ModernArticleCard from "./ModernArticleCard";
import "./insightsCategorySection.scss";

/**
 * A section that displays a category of insights (Articles, White Papers, etc.)
 * with a header, icon, grid of cards, and a "View All" link.
 *
 * Props:
 *  - title: string (e.g. "Articles")
 *  - titleAccent: string (highlighted portion of title)
 *  - subtitle: string
 *  - icon: JSX element
 *  - items: array of article-shaped objects
 *  - viewAllLink: string (e.g. "/insights/articles")
 *  - viewAllLabel: string (e.g. "View All Articles")
 *  - accentColor: string (optional, defaults to brand red)
 *  - isLoading: boolean
 */
function InsightsCategorySection({
  title = "Articles",
  titleAccent = "",
  subtitle = "",
  icon,
  items = [],
  viewAllLink = "",
  viewAllLabel = "View All",
  accentColor,
  isLoading = false,
}) {
  const renderTitle = () => {
    if (!titleAccent) return title;
    const idx = title.indexOf(titleAccent);
    if (idx === -1) return title;
    const before = title.substring(0, idx);
    const after = title.substring(idx + titleAccent.length);
    return (
      <>
        {before}
        <span className="category-title-accent">{titleAccent}</span>
        {after}
      </>
    );
  };

  if (isLoading) {
    return (
      <section className="insights-category-section">
        <div className="body-container">
          <div className="category-header">
            <div className="category-header-left">
              <div className="skeleton-heading" />
              <div className="skeleton-subtext" />
            </div>
          </div>
          <div className="category-grid">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="loading-card-skeleton">
                <div className="skeleton-image" />
                <div className="skeleton-content">
                  <div className="skeleton-tag" />
                  <div className="skeleton-title" />
                  <div className="skeleton-title skeleton-title--short" />
                  <div className="skeleton-desc" />
                  <div className="skeleton-desc skeleton-desc--short" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!items || items.length === 0) return null;

  return (
    <section
      className="insights-category-section"
      style={accentColor ? { "--category-accent": accentColor } : undefined}
    >
      <div className="body-container">
        {/* Section Header */}
        <RevealAnimation animation="slide-up" delay={0.1}>
          <div className="category-header">
            <div className="category-header-left">
              {icon && <div className="category-icon">{icon}</div>}
              <div>
                <h2 className="category-title">{renderTitle()}</h2>
                {subtitle && <p className="category-subtitle">{subtitle}</p>}
              </div>
            </div>
            {viewAllLink && (
              <Link to={viewAllLink} className="category-view-all">
                {viewAllLabel}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            )}
          </div>
        </RevealAnimation>

        {/* Cards Grid */}
        <div className="category-grid">
          {items.map((item, index) => (
            <RevealAnimation
              key={item.slug || index}
              animation="fade"
              delay={0.1 + index * 0.08}
            >
              <ModernArticleCard article={item} index={index} />
            </RevealAnimation>
          ))}
        </div>
      </div>
    </section>
  );
}

export default InsightsCategorySection;
